import React from 'react';
import { CardContainer } from './CardContainer';
import { SectionHeader } from './SectionHeader';
import { Typography } from '../design-system/typography';
import { colors, spacing } from '../design-system/tokens';

// Percent icon SVG (matching design system style)
const PercentIcon: React.FC = () => (
  <svg 
    width="18" 
    height="24" 
    viewBox="0 0 18 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M4.5 9C6.433 9 8 7.433 8 5.5S6.433 2 4.5 2S1 3.567 1 5.5S2.567 9 4.5 9ZM4.5 9L13.5 2M13.5 15C15.433 15 17 13.433 17 11.5S15.433 8 13.5 8S10 9.567 10 11.5S11.567 15 13.5 15Z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface ContractCommissionData {
  commissionRate: number; // As percentage (e.g., 2.0 for 2.0%)
  marginShare: number; // As percentage (e.g., 50.0 for 50.0%)
  totalCommission: number; // Total commission amount across all shipments
}

interface ContractCommissionCardProps {
  data: ContractCommissionData;
  className?: string;
}

export const ContractCommissionCard: React.FC<ContractCommissionCardProps> = ({ 
  data,
  className = '' 
}) => {
  const formatPercentage = (value: number): string => {
    return `     ${value.toFixed(1)}%`; // Add padding for alignment
  };

  const formatCurrency = (amount: number): string => {
    return `$ ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Data row component for consistent formatting with proper number alignment
  const DataRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      height: '18px',
      gap: spacing.xl 
    }}>
      <Typography 
        variant="ledgerLabel" 
        as="div"
        style={{ 
          color: colors.text.primary,
          textTransform: 'uppercase',
          flex: 1,
          maxWidth: '220px'
        }}
      >
        {label}
      </Typography>
      <Typography 
        variant="ledgerNumbers" 
        as="div"
        style={{ 
          color: colors.text.primary,
          textAlign: 'right',
          width: '100px', // Fixed width for alignment
          fontFamily: '"Reddit Mono", monospace',
          whiteSpace: 'pre' // Preserve spaces for padding
        }}
      >
        {value}
      </Typography>
    </div>
  );

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      <CardContainer variant="secondary">
        {/* Header */}
        <SectionHeader 
          title="Commission & Margin"
          icon={<div style={{ width: '18px', height: '24px' }}><PercentIcon /></div>}
          showBottomBorder={true}
        />

        {/* Commission Rate */}
        <DataRow 
          label="Commission Rate" 
          value={formatPercentage(data.commissionRate)} 
        />

        {/* Margin Share */}
        <DataRow 
          label="Margin Share" 
          value={formatPercentage(data.marginShare)} 
        />

        {/* Dotted Divider */}
        <div style={{ 
          height: '1px',
          backgroundImage: 'repeating-linear-gradient(to right, #D7DADB 0px, #D7DADB 4px, transparent 4px, transparent 8px)',
          margin: `${spacing.sm} 0`
        }} />

        {/* Total Commission */}
        <DataRow 
          label="Total Commission" 
          value={formatCurrency(data.totalCommission)}
        />
      </CardContainer>
    </div>
  );
};

export default ContractCommissionCard;